import { createContext, useMemo, useState, useContext, ReactNode } from 'react'
import { GladlyConfig, GladlyAnswerIndex } from 'libs/utils'

interface SearchContextProps {
  config: GladlyConfig | null
  setConfig: (config: GladlyConfig | null) => void
  currentSearch: string | null
  setCurrentSearch: (search: string | null) => void
  isSearching: boolean
  setIsSearching: (isSearching: boolean) => void
  results: GladlyAnswerIndex[] | null
  setResults: (results: GladlyAnswerIndex[] | null) => void
  lastSearchPerformed: string | null
  setLastSearchPerformed: (search: string | null) => void
}

const GladlySearchContext = createContext<SearchContextProps>({
  config: null,
  setConfig: () => {},
  currentSearch: null,
  setCurrentSearch: () => {},
  isSearching: false,
  setIsSearching: () => {},
  results: null,
  setResults: () => {},
  lastSearchPerformed: null,
  setLastSearchPerformed: () => {},
})

export const GladlySearchProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<GladlyConfig | null>(null)

  const [results, setResults] = useState<GladlyAnswerIndex[] | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [currentSearch, setCurrentSearch] = useState<string | null>(null)
  const [lastSearchPerformed, setLastSearchPerformed] = useState<string | null>(
    null,
  )

  const value = useMemo(
    () => ({
      config,
      setConfig,
      currentSearch,
      setCurrentSearch,
      isSearching,
      setIsSearching,
      results,
      setResults,
      // Last search performed: used to resume search
      lastSearchPerformed,
      setLastSearchPerformed,
    }),
    [config, currentSearch, isSearching, lastSearchPerformed, results],
  )

  return (
    <GladlySearchContext.Provider value={value}>
      {children}
    </GladlySearchContext.Provider>
  )
}

export const useGladlySearchContext = () => useContext(GladlySearchContext)
